import environment from "../../env.js";
import { AuthenticationClient } from "authing-js-sdk";
import eventBus from "./eventBus.js";
import URL from "url";
import { AUTH_STORAGE_USER_INFO_KEY, AUTH_STORAGE_ID_TOKEN_KEY, storageUtils } from "./storageUtils.js";

const env = environment.env;

const authClient = new AuthenticationClient({
  appId: environment.authingAPI[env].appId,
});

// Doc: https://docs.authing.cn/v2/reference/error-code.html
const AUTHING_ERROR_CODE = {
  SERVER_BUSY: 1000,
};

var alertLogout = false;

const convertUser = (user) => {
  return user ? { ...user, uid: user.id, displayName: user.name } : user;
};

const convertCredential = (user) => ({
  credential: { idToken: user.token, accessToken: "" },
});

const forceRedirectToReLogin = (msg) => {
  if (!alertLogout) {
    alertLogout = true;
    alert(msg);
  }
  location.href = "/";
  return;
};

function checkUserLogout(user) {
  if (!authApi.currentUser.uid) {
    // no user login
    return;
  }
  // user already login, check user status in authing
  if (!user && URL.parse(location.href).path !== "/") {
    forceRedirectToReLogin("此帐号已从其他设备登出，请重新登录！");
    throw "User logouted";
  }
}

const getExpiresAt = (time) => {
  return new Date(time).getTime();
};

const cleanAuthStorage = () => {
  storageUtils.removeItem(AUTH_STORAGE_USER_INFO_KEY);
  storageUtils.removeItem(AUTH_STORAGE_ID_TOKEN_KEY);
};

const authApi = {
  currentUser: {
    getIdToken: function (bool) {
      return authApi.getIdToken(bool);
    },
    displayName: "",
    email: "",
    uid: "",
  },
  onAuthStateChanged: function (callback) {
    authApi
      .getCurrentUser()
      .then((user) => {
        callback(user);
      })
      .catch((error) => {
        console.log(error);
        callback(null);
      });

    eventBus.subscribe("onAuthStateChanged", callback);
  },
  getCurrentUser: function () {
    return new Promise(function (resolve) {
      let localStorageUserInfo = storageUtils.getItem(AUTH_STORAGE_USER_INFO_KEY);
      if (localStorageUserInfo) {
        return resolve(localStorageUserInfo);
      }

      authClient
        .getCurrentUser()
        .then((user) => {
          checkUserLogout(user);
          if (!user) {
            return resolve(null);
          }

          const userInfo = convertUser(user);
          storageUtils.setItem(AUTH_STORAGE_USER_INFO_KEY, userInfo, getExpiresAt(user.tokenExpiredAt));
          resolve(userInfo);
        })
        .catch((error) => {
          console.log(error);
          resolve(null);
        });
    });
  },
  getIdToken: async function (bool = false) {
    try {
      if (bool) {
        console.log("refreshToken ...");
        const tokenInfo = await authClient.refreshToken();
        storageUtils.setItem(AUTH_STORAGE_ID_TOKEN_KEY, tokenInfo.token, getExpiresAt(tokenInfo.exp * 1000));
        console.log("refreshToken finished");
      }

      const localStorageIdToken = storageUtils.getItem(AUTH_STORAGE_ID_TOKEN_KEY);
      if (localStorageIdToken) {
        return localStorageIdToken;
      }

      const user = await authClient.getCurrentUser();
      if (!user) {
        throw "authClient.getCurrentUser failed - user is null";
      }
      storageUtils.setItem(AUTH_STORAGE_ID_TOKEN_KEY, user.token, getExpiresAt(user.tokenExpiredAt));
      return user.token;
    } catch (e) {
      if (e.code === AUTHING_ERROR_CODE.SERVER_BUSY) {
        return "";
      }
      console.error("authApi.getIdToken failed:", e);
      forceRedirectToReLogin("登录失效，请重新登录");
      cleanAuthStorage();
      return "";
    }
  },
  loginByUsername: function (name, password) {
    return authClient.loginByUsername(name, password);
  },
  loginByEmail: function (email, password) {
    return new Promise(function (resolve, reject) {
      authClient
        .loginByEmail(email, password)
        .then((user) => {
          cleanAuthStorage();
          const userInfo = convertUser(user);
          resolve(userInfo);
          eventBus.publish("onAuthStateChanged", userInfo);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  logout: async function () {
    try {
      // force refresh to prevent sharing account side effect: When user A logout then the other users should logout, too.
      await authClient.refreshToken();
      await authClient.logout();
    } catch (e) {
      // skip authClient logout fail.
    }
    cleanAuthStorage();
    return;
  },
  loginWithPopup: function (protocol, identifier) {
    return new Promise(function (resolve, reject) {
      authClient.enterprise.authorize(protocol, identifier, {
        onSuccess: (user) => {
          cleanAuthStorage();
          resolve(convertCredential(user));
        },
        onError: (code, message) => {
          console.log(code, message);
          reject(error);
        },
        position: {
          w: 480,
          h: 480,
        },
      });
    });
  },
};

export default authApi;
